import { forwardRef, useEffect, useRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import {
  getBaseTextSizeClassName,
  getBaseTypographyToneClassName,
  resolveBaseAppearance,
} from './theme';
import type { BaseLinkProps } from './types';
import { cn } from './utils';
import { safeDisplayUrl } from '../safeDisplayUrl';

export const BaseLink = forwardRef<HTMLAnchorElement, BaseLinkProps>(
  function BaseLink(
    {
      children,
      openOnMount = false,
      size = 'md',
      tone = 'primary',
      appearance,
      className,
      classNames,
      style,
      ...linkProps
    },
    ref,
  ) {
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const href = safeDisplayUrl(linkProps.href, 'link');
    const rejected = linkProps.href !== undefined && href === undefined;
    const attempted = useRef<string>();
    useEffect(() => {
      if (!openOnMount || !href || linkProps.target !== '_blank' || linkProps['aria-disabled'] === true || linkProps['aria-disabled'] === 'true' || attempted.current === href) return;
      attempted.current = href;
      window.open(href, '_blank', 'noopener,noreferrer');
    }, [openOnMount, href, linkProps.target, linkProps['aria-disabled']]);

    return (
      <a
        {...componentAttributes}
        {...linkProps}
        href={href}
        aria-disabled={rejected ? true : linkProps['aria-disabled']}
        onClickCapture={event => {
          if (rejected) { event.preventDefault(); event.stopPropagation(); return; }
          linkProps.onClickCapture?.(event);
        }}
        onClick={event => {
          if (rejected) { event.preventDefault(); event.stopPropagation(); return; }
          linkProps.onClick?.(event);
        }}
        onAuxClickCapture={event => {
          if (rejected) { event.preventDefault(); event.stopPropagation(); return; }
          linkProps.onAuxClickCapture?.(event);
        }}
        ref={ref}
        data-monkeys-component="link"
        data-slot="root"
        data-size={size}
        data-tone={tone}
        className={cn(
          theme.slots.linkRoot,
          getBaseTextSizeClassName(theme, size),
          getBaseTypographyToneClassName(theme, tone),
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.link,
          ...style,
        }}
      >
        {children}
      </a>
    );
  },
);
